import React from "react";
import {
  Box,
  Stack,
  Button,
  Card,
  Divider,
  Grid,
  Typography,
  Tooltip,
  Container,
  Paper,
  Avatar,
  Chip,
} from "@mui/material";
import {
  ArrowOutward as ArrowOutwardIcon,
  Groups as GroupsIcon,
  Construction as ConstructionIcon,
  Diversity3 as Diversity3Icon,
  School as SchoolIcon,
} from "@mui/icons-material";

const StudentGroups = () => {
  const engsoc_roles = [
    {
      role: "Prez & VP",
      blurb:
        "Elected for each society, these Executives serve for 16 months contributing a significant amount of time and effort to the success of EngSoc and our Waterloo Engineeering way of life. There are 4 Vice-Presidents (VPs) - Academic, Communications, Finance and Student Life.",
    },
    {
      role: "Commissioner",
      blurb:
        "Appointed at the end of each term for the next on-stream term, they act as liasons between Directors and Executives. They ensure the smooth running of EngSoc by helping out with some of the bigger services and events run each term.",
    },
    {
      role: "Event Directors",
      blurb:
        "Each term, nearly 100 Directors are appointed by EngSoc Executives to help run events for a duration of 4 months. Applications are open regardless of experience or year of study. With so many positions, there is at least one that would be a good fit for you in line with your interests.",
    },
    {
      role: "Class Reps",
      blurb:
        "At the beginning of a term, each engineering class elects 2 people to represent them in EngSoc Council Meetings. They aim to keep you informed of EngSoc's events, services, representation, to help the class be more involved and raise class spirit. They should know what is going on and get you in touch with the right people if you have any questions/concerns.",
    },
  ];

  const affiliate_logos = [
    {
      path: "/images/affiliate-logos/engiqueers.png",
      alt: "Engiqueers",
      site: "https://uwaterloo.ca/engineering/engiqueers",
    },
    {
      path: "/images/affiliate-logos/nsbe.png",
      alt: "NSBE",
      site: "https://nsbe.uwaterloo.ca/",
    },
    {
      path: "/images/affiliate-logos/wie.avif",
      alt: "Women in Engineering",
      site: "https://uwaterloo.ca/women-in-engineering/",
    },
    {
      path: "https://uwaterloo.ca/engineering-equity-diversity/sites/default/files/uploads/images/aises-logo_0.png",
      alt: "AISES",
      site: "https://www.instagram.com/uw_aises/",
    },
    {
      path: "/images/affiliate-logos/iron_warrior.png",
      alt: "Iron Warrior",
      site: "https://iwarrior.uwaterloo.ca/",
    },
    {
      path: "/images/affiliate-logos/WRTT.webp",
      alt: "With Respect To Time (Engineering Jazz Band)",
      site: "https://www.engjazzband.ca/",
    },
  ];

  const Header = () => (
    <Paper
      elevation={0}
      sx={{
        bgcolor: "#5D0096",
        color: "white",
        borderRadius: 0,
        py: 6,
        mb: 4,
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={4} alignItems="center" textAlign="center">
          <Typography
            variant="h2"
            fontWeight="bold"
            sx={{
              fontSize: { xs: "2.2rem", md: "3.2rem" },
              textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
            }}
          >
            Get Involved!
          </Typography>

          <Typography
            variant="h5"
            sx={{
              maxWidth: 900,
              lineHeight: 1.6,
              opacity: 0.95,
            }}
          >
            There are many ways to plug into our campus communities. Explore
            design teams, the Engineering Society, affiliate groups, and WUSA
            clubs.
          </Typography>
        </Stack>
      </Container>
    </Paper>
  );

  const Section = ({ title, subtitle, icon, children, gradient = false }) => (
    <Container maxWidth="lg" sx={{ mb: 6 }}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2.5, md: 4 },
          borderRadius: 4,
          ...(gradient
            ? {
                background: "linear-gradient(135deg, #C2A8F0 0%, #5D0096 80%)",
                color: "white",
              }
            : {
                backgroundColor: "white",
              }),
        }}
      >
        <Stack spacing={3}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {icon}
            <Typography variant="h4" fontWeight="bold">
              {title}
            </Typography>
            {subtitle && (
              <Chip
                label={subtitle}
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: gradient ? "rgba(255,255,255,0.2)" : "#5D0096",
                  color: "white",
                  fontWeight: 600,
                }}
              />
            )}
          </Stack>

          <Divider
            sx={{ borderColor: gradient ? "rgba(255,255,255,0.3)" : "#eee" }}
          />

          {children}
        </Stack>
      </Paper>
    </Container>
  );

  const SDCSection = () => (
    <Section
      title="Sedra Design Centre (SDC) Student Design Teams"
      icon={<ConstructionIcon sx={{ color: "#5D0096" }} />}
      gradient
    >
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} md={5}>
          <Box display="flex" justifyContent="center">
            <img
              src="/images/SDC.png"
              alt="Design Teams in Action"
              style={{
                objectFit: "contain",
                width: "100%",
                maxWidth: 480,
                borderRadius: 16,
                boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                background: "white",
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={7}>
          <Stack spacing={2}>
            <Typography variant="body1" sx={{ lineHeight: 1.7, opacity: 0.95 }}>
              The <strong>SDC</strong> is a state-of-the art 20,000 square foot
              facility home to over 20 of the Faculty of Engineering student
              competition teams. Equipped with spaces that facilitate sanding,
              painting, engine testing, electronics assembly and more, it is the
              perfect place to use and learn most skills you need. In order to
              join, just email them, join their Discord, or simply walk into
              their team bay/meeting. The time commitment also depends on you!
            </Typography>
            <Box>
              <Button
                variant="contained"
                color="engPrimary"
                endIcon={<ArrowOutwardIcon />}
                onClick={() =>
                  window.open(
                    "https://uwaterloo.ca/sedra-student-design-centre/directory-teams",
                    "_blank",
                  )
                }
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Go to SDC Design Team Directory
              </Button>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Section>
  );

  const RoleCard = ({ role, blurb }) => (
    <Card
      elevation={2}
      sx={{
        height: "100%",
        borderRadius: 3,
        transition: "all 0.25s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 8,
        },
      }}
    >
      <Box p={2.5}>
        <Stack spacing={1}>
          <Typography variant="h6" align="center" fontWeight="bold">
            {role}
          </Typography>
          <Divider />
          <Typography variant="body2" align="center" color="text.secondary">
            {blurb}
          </Typography>
        </Stack>
      </Box>
    </Card>
  );

  const EngSocSection = () => (
    <Section
      title="Waterloo Engineering Society (EngSoc)"
      icon={<SchoolIcon sx={{ color: "#5D0096" }} />}
      subtitle="Events • Services • Advocacy"
    >
      <Stack spacing={3}>
        <Box display="flex" justifyContent="center">
          <Avatar
            src="/images/resource-logos/engsoc.png"
            alt="EngSoc Logo"
            sx={{
              width: 120,
              height: 120,
              borderRadius: 3,
              border: "2px solid #5D0096",
              boxShadow: 2,
              bgcolor: "white",
            }}
          />
        </Box>

        <Typography
          variant="body1"
          align="center"
          sx={{ maxWidth: 1000, mx: "auto" }}
        >
          The <strong>Engineering Society (EngSoc)</strong> is the official
          representative body for undergraduate students in the Faculty of
          Engineering. It is dedicated to enhancing the academic, social, and
          professional aspects of the engineering student experience. EngSoc
          predates WUSA, has the most active membership of all faculty societies
          on campus, and has the largest membership of all engineering societies
          in Canada.
        </Typography>

        <Typography
          variant="body1"
          align="center"
          sx={{ maxWidth: 1000, mx: "auto" }}
        >
          The Society is founded on 3 pillars: they organize events, provide
          services, and demonstrate advocacy for all undergraduate Engineering
          students. EngSoc hosts over 50 fun events every term, including
          socials, semi-formals, talent shows, guest speakers, athletic events,
          and more. In terms of services, EngSoc runs everything from Coffee and
          Donut shops and swag stores, to conference opportunities and
          professional workshops. The EngSoc Office at CPH 1327 also houses
          resources, printing, games, and equipment rentals. As the official
          representative body, we represent roughly 8000 engineering students to
          the faculty, university, and even country. This is organized through
          faculty councils, university senates, engineering student conferences,
          and last but not least, council meetings, which is the driving force
          of Engsoc's advocacy where students vote on key issues facing the
          Society.
        </Typography>

        <Typography
          variant="body1"
          align="center"
          sx={{ maxWidth: 1000, mx: "auto" }}
        >
          One key thing to note is the Society is separated into two leadership
          teams, A Society and B Society, that alternate every term as students
          are constantly switching between study and coop terms. In odd years,
          Winter and Fall terms are A Society and Spring term is B Society and
          vice versa for even years. But you can find more on what both are up
          to on the EngSoc website and @uwengsoc on Instagram.
        </Typography>

        <Box display="flex" justifyContent="center">
          <Button
            variant="contained"
            color="engPrimary"
            endIcon={<ArrowOutwardIcon />}
            onClick={() => window.open("https://linktr.ee/uwengsoc", "_blank")}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              minWidth: 280,
            }}
          >
            Go to EngSoc LinkTree
          </Button>
        </Box>

        <Grid
          container
          spacing={2}
          justifyContent="center"
          alignItems="stretch"
        >
          {engsoc_roles.map((item) => (
            <Grid item key={item.role} xs={12} sm={6} md={3}>
              <RoleCard role={item.role} blurb={item.blurb} />
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Section>
  );

  const AffiliatesSection = () => (
    <Section
      title="EngSoc Affiliates & Constituent Groups"
      icon={<Diversity3Icon sx={{ color: "#5D0096" }} />}
    >
      <Stack spacing={3} alignItems="center" textAlign="center">
        <Typography
          variant="body1"
          sx={{ maxWidth: 900, color: "text.secondary" }}
        >
          There are many ways to supplement your academics while representing
          your engineering spirit. Writing for <strong>The Iron Warrior</strong>{" "}
          (Engineering Newspaper), performing in{" "}
          <strong>With Respect to Time</strong> (Eng's jazz band), getting
          involved with <strong>Engiqueers</strong> to help organize events,
          there are many many ways to get involved with EngSoc's many affiliate
          groups! Affiliates are included in the Engineering Society governing
          documents and include many groups, some of which are shown below.
        </Typography>

        <Button
          variant="contained"
          color="engPrimary"
          endIcon={<ArrowOutwardIcon />}
          onClick={() =>
            window.open(
              "https://www.engsoc.uwaterloo.ca/about-us/affiliates/",
              "_blank",
            )
          }
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            minWidth: 280,
          }}
        >
          Go to the EngSoc Affiliates Page
        </Button>

        <Typography variant="h6" sx={{ mt: 2 }}>
          Alternatively, click any of the logos below to learn more!
        </Typography>

        <Grid
          container
          spacing={2}
          justifyContent="center"
          alignItems="stretch"
        >
          {affiliate_logos.map((logo) => (
            <Grid item key={logo.alt} xs={12} sm={6} md={3}>
              <Tooltip title={logo.site}>
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 8,
                    },
                    cursor: "pointer",
                  }}
                  onClick={() => window.open(logo.site, "_blank")}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: 120,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "& img": {
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        transition: "transform 0.2s ease-in-out",
                      },
                      "&:hover img": {
                        transform: "scale(1.05)",
                        opacity: 0.95,
                      },
                    }}
                  >
                    <img src={logo.path} alt={logo.alt} />
                  </Box>
                </Card>
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Section>
  );

  const WUSASection = () => (
    <Section
      title="WUSA Clubs"
      icon={<GroupsIcon sx={{ color: "#5D0096" }} />}
      subtitle="Campus-wide Communities"
    >
      <Stack spacing={3} alignItems="center" textAlign="center">
        <Box
          sx={{
            width: "80%",
            height: 120,
            "& img": {
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            },
          }}
        >
          <img src="/images/resource-logos/wusa.png" alt="WUSA Logo" />
        </Box>
        <Typography
          variant="body1"
          sx={{ maxWidth: 800, color: "text.secondary" }}
        >
          Waterloo Undergraduate Student Association (WUSA) supports clubs
          dedicated to every interest and activity under the sun, ranging from
          hobbies, studies, charities, social movements, religions and more! You
          can check out their webpage for more information and to find a club
          that you would like to join.
        </Typography>
        <Button
          variant="contained"
          color="engPrimary"
          endIcon={<ArrowOutwardIcon />}
          onClick={() => window.open("https://wusa.ca/clubs/", "_blank")}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            minWidth: 240,
          }}
        >
          Go to WUSA Clubs Page
        </Button>
      </Stack>
    </Section>
  );

  return (
    <Box>
      <Header />
      <SDCSection />
      <EngSocSection />
      <AffiliatesSection />
      <WUSASection />
    </Box>
  );
};

export default StudentGroups;
